import { PersonaFieldTypes } from '@app/modules/personas/enums/persona-fields-types.enum'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { NotificationChannel, resendConfirmationSuccessToastBody } from '@database/models/resend-confirmation-pricing'
import { DatabaseService, OrderTypes, ResendConfirmationSingleChannel } from '@database/services/database.service'
import { Subject } from 'rxjs'
import { RegistrationData, RegistrationHandledBy, RegistrationPrintEntry } from './registration-data.interface'
import { ApprovalStatus } from './registration-status.enum'

/**
 * Defines a single registration in a data source
 */
export class Registration {
  refunded: boolean

  attendanceDays: string[]
  /** Original registration details */
  private _source: RegistrationData
  /** Public getter for source data */
  get source() {
    return this._source
  }
  /** Registration order in the datasource list */
  private _order: number

  private _form: Persona

  payment_status: string

  /** Calculated fields are those displayed on the datasource table, used to hide certain values */
  public calculatedFields: { [key: string]: { hidden: boolean; value: string | string[]; hovered: boolean } }
  /** Toggled to view extra registration details
   * @deprecated
   */
  public toggled = false
  /** Whether a registration is selected */
  public selected = false
  /**
   * Is the registration flagged?
   */
  public flagged = false
  /**
   * Is the registration approved? Used to control registrations
   */
  public approved = ApprovalStatus.DECLINED
  /**
   * Approved by
   */
  public approvedBy: RegistrationHandledBy
  /**
   * Declined by
   */
  public declinedBy: RegistrationHandledBy
  /**
   * Is the registration performing an async operation? used to control loader
   */
  public processing = false
  /**
   * Did the registration attend the event?
   */
  public attended = false
  /** Registration fields - data collection */
  public fields: { [key: string]: string }
  /** Mail status of the last sent confirmation email */
  public mailStatus = ''
  /** Whether we can resend the confirmation email or not */
  public canResendMail = false
  /** Total number of sent emails */
  public sentMails = 0
  /** Total numbers of sent emails that reached the inbox */
  public deliveredMails = 0
  /** Total number of emails - sent, recieved, blocked, failed, bounced */
  public mailCount = 0

  public entry_status: ApprovalStatus = 0

  /** SMS status of the last sent confirmation email */
  public smsStatus = ''
  /** Total number of sent SMS */
  public sentSms = 0
  /** Total numbers of sent SMS that reached the user */
  public deliveredSms = 0
  /** Total number of SMS - sent, recieved, blocked, failed, bounced */
  public smsCount = 0

  /** WhatsApp status of the last sent confirmation email */
  public whatsappStatus = ''
  /** Total number of sent WhatsApp */
  public sentWhatsapp = 0
  /** Total numbers of sent WhatsApp that reached the user */
  public deliveredWhatsapp = 0
  /** Total number of WhatsApp - sent, recieved, blocked, failed, bounced */
  public whatsappCount = 0
  /** Total number of sent confirmation WhatsApp */
  public sentConfirmationWhatsapp = 0
  /** Total number of sent invitation WhatsApp */
  public sentInvitationWhatsapp = 0
  /** Total number of sent reminder WhatsApp */
  public sentReminderWhatsapp = 0

  /** Settled status of the registrant (If you're looking into whether a registration is paid or not, check the tags.settled instead) */
  public settledStatus: OrderTypes = null

  /** Registration order */
  public readonly order: number
  /** Registration ID */
  public readonly id: string
  /** Registration barcode */
  public readonly barcode: string
  /** Registration tags */
  public readonly tags: any
  /**
   * returns the edit url for the registration
   */
  public readonly editUrl: string
  /** Registration confirmation url */
  public readonly confirmationUrl: string
  /** Created at */
  public readonly created_at: string
  public maximum_registrations: number
  /** Child count for GROUP registrations */
  public readonly child_count: number
  /** Badge print history for this attendee */
  public readonly prints: RegistrationPrintEntry[]

  /**
   * Registration has changed
   */
  public changed$: Subject<boolean> = new Subject()

  public selectedSeats = ''

  /**
   * Registration payments
   */
  payments: any[] = []

  /**
   * Minimal current-user context (provided by AuthService).
   * This model must not read from storage directly.
   */
  private readonly _userContext?: {
    displayName: string
    approvedBy: { name: string; uuid?: string; source: 'planning' } | null
  }

  /**
   * Creates a new registration model
   * @param source Registration data
   * @param order Registration order
   * @param service Database service
   */
  constructor(
    source: RegistrationData,
    order: number,
    private service: DatabaseService,
    userContext?: { displayName: string; approvedBy: { name: string; uuid?: string; source: 'planning' } | null }
  ) {
    this._userContext = userContext
    this._source = source
    this.order = order
    this.id = source.id
    this.barcode = source.barcode
    this.tags = source.tags
    this.editUrl = `/events/${this._source.event}/forms/${this._source.form}/registrations/${this._source.id}/edit`
    this.confirmationUrl = `/events/${this._source.event}/forms/${this._source.form}/registrations/${this._source.id}/confirmation`
    this.approved = source.tags?.approved
    this.approvedBy = source.approved_by
    this.declinedBy = source.declined_by
    this.payment_status = source.payment_status
    // Backward compatibility: prioritize tags.flagged, then check third_parts.flags
    // tags.flagged is the source of truth for active flag status
    // third_parts.flags preserves history/comments even when unflagged
    if (source.tags?.flagged !== undefined) {
      this.flagged = !!source.tags.flagged
    } else if (source.third_parts?.flags && Array.isArray(source.third_parts.flags) && source.third_parts.flags.length > 0) {
      // Fallback: if tags.flagged doesn't exist, check flags array for backward compatibility
      this.flagged = true
    } else {
      this.flagged = false
    }
    this.attended = source.attendance_tags?.attended
    this.settledStatus = source.settled_status
    this.entry_status = source.entry_status
    this.attendanceDays = source.attendance_tags?.checkins_days
    this.refunded = source.refunded
    this.generateFieldsAndCalculatedFields()
    this.calculateMailStatus()
    this.calculateSMSStatus()
    this.calculateWhatsappStatus()
    this.created_at =
      new Date(source.created_at).toDateString() == 'Invalid Date' ? new Date(source.created_at.slice(0, 10))?.toDateString() : new Date(source.created_at)?.toDateString()
    this._setSelectedSeats()
    this.payments = source.payments
    this.child_count = source.child_count || 0
    this.prints = Array.isArray(source.prints) ? source.prints : []
    this._form = service.forms.value.find((form) => form.slug === source.form)
    this.maximum_registrations = this._form.type === PersonaTypes.GROUP ? source.third_parts?.max_registration || this._getChildPersona().options.maximumEntries : 0
  }
  /**
   * Sets the mailStatus & mailCount properties of the registration
   * @returns void
   */
  calculateMailStatus(emitChange = false) {
    if (!Object.prototype.hasOwnProperty.call(this._source, 'mail_status') && !Object.prototype.hasOwnProperty.call(this._source.mail_status, 'last_status')) {
      this.mailStatus = 'N/A'
      this.mailCount = 0
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    this.mailCount = this._source.mail_status.delivered_mail_count
    if (this._source.mail_status.last_status === null) {
      this.mailStatus = ''
      this.mailCount = this._source.mail_status.confirmation_mail_count
      this.canResendMail = false
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    // Pending is shown even when entry is still in review (legacy precedence)
    if (this._source.mail_status.last_status === 'Pending') {
      this.mailStatus = 'Pending'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      this.canResendMail = true
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.entry_status === ApprovalStatus.PENDING_REVIEW) {
      this.mailStatus = ''
      this.mailCount = this._source.mail_status.confirmation_mail_count
      this.canResendMail = false
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Sent') {
      this.mailStatus = 'Sent'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      this.canResendMail = true
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Delivery') {
      this.canResendMail = true
      this.mailStatus = 'Delivered'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Failed') {
      this.canResendMail = true
      this.mailStatus = 'Failed'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Bounce') {
      this.canResendMail = false
      this.mailStatus = 'Bounced'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Complaint') {
      this.canResendMail = true
      this.mailStatus = 'Complaint'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }

    if (this._source.mail_status.last_status === 'Opened') {
      this.canResendMail = true
      this.mailStatus = 'Opened'
      this.mailCount = this._source.mail_status.confirmation_mail_count
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }
  }
  /**
   * Sets the smsStatus & smsCount properties of the registration
   * @returns void
   */
  calculateSMSStatus(emitChange = false) {
    if (!Object.prototype.hasOwnProperty.call(this._source, 'sms_status') && !Object.prototype.hasOwnProperty.call(this._source.sms_status, 'last_status')) {
      this.smsStatus = 'N/A'
      this.smsCount = 0
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }
    this.smsCount = this._source.sms_status.sent_sms_count || this._source.sms_status.sms_count || 0
    switch (this._source.sms_status.last_status) {
      case 'Sent':
        this.smsStatus = 'Received'
        if (emitChange) {
          this.changed$.next(null)
        }
        return
      default:
        this.smsStatus = this._source.sms_status.last_status
        if (emitChange) {
          this.changed$.next(null)
        }
        return
    }
  }
  /**
   * Sets the whatsappStatus & whatsappCount & whatsappDeliveredCount properties of the registration
   * @returns void
   */
  calculateWhatsappStatus(emitChange = false) {
    if (
      !Object.prototype.hasOwnProperty.call(this._source, 'whatsapp_status') ||
      this._source.whatsapp_status == null ||
      !Object.prototype.hasOwnProperty.call(this._source.whatsapp_status, 'last_status')
    ) {
      this.whatsappStatus = 'N/A'
      this.whatsappCount = 0
      if (emitChange) {
        this.changed$.next(null)
      }
      return
    }
    this.whatsappCount = this._source.whatsapp_status.whatsapp_count || 0
    switch (this._source.whatsapp_status.last_status) {
      case 'sent':
        this.whatsappStatus = 'Sent'
        break
      case 'read':
        this.whatsappStatus = 'Read'
        break
      case 'failed':
        this.whatsappStatus = 'Failed'
        break
      case 'delivered':
        this.whatsappStatus = 'Delivered'
        break
      default:
        this.whatsappStatus = ''
        break
    }
    if (emitChange) this.changed$.next(null)
  }

  /**
   * Unhides a specific field of an unpaid registration
   * @param fieldName field name to unhide
   */
  unhide(fieldName) {
    this.processing = true
    this.changed$.next(null)
    this.service.unhideField(fieldName, this._source.event, this._source.form, this._source.barcode).subscribe(
      (result) => {
        this.generateFieldsAndCalculatedFields(result)
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * Generates fields and calculated fields property, also displays the hide/ show mechanics of a registration instead of asterisks
   * @param replace Replace is used when unhiding a certain field if it's hidden
   */
  generateFieldsAndCalculatedFields(replace: { [key: string]: string | string[] } = null) {
    this.fields = {}
    Object.keys(this._source.fields).forEach((o) => {
      const element = this._form?.form_elements?.find((element) => element.name === o)
      if ([PersonaFieldTypes.LINE_SEPARATOR, PersonaFieldTypes.INFORMATION_MESSAGE, PersonaFieldTypes.WARNING_MESSAGE].includes(element?.form_element_type_id)) return
      this.fields[o] = this._source.fields[o] as string
    })
    if (replace) {
      Object.keys(replace).forEach((key) => {
        if (Array.isArray(replace[key])) {
          this.fields[key.replace('fields.', '')] = (replace[key] as string[]).join('<br>')
          return
        }
        this.fields[key.replace('fields.', '')] = replace[key] as string
      })
    }
    this.calculatedFields = {}
    Object.keys(this.fields).forEach((o) => {
      this.calculatedFields[o] = { hidden: /^[*\s]+$/.test(this.fields[o]), value: this.fields[o], hovered: false }
    })
  }
  /**
   * Creates a registration
   * @param registrationData Registration data
   * @param order Registration order
   * @param databaseService Database service
   * @returns new registration
   */
  static create(registrationData: RegistrationData, order: number, databaseService: DatabaseService): Registration {
    return new Registration(registrationData, order, databaseService)
  }
  /**
   * Creates multiple registrations for a given array of registrations
   * @param registrationsData array of registrations
   * @param service Database service
   * @param attendanceSpot Attendance spot name
   * @returns array of new registrations
   */
  static createFromArray(
    registrationsData: RegistrationData[],
    service: DatabaseService,
    attendanceSpot: string,
    userContext?: { isAdmin: boolean; approvedBy: { name: string; uuid?: string; source: 'planning' } | null; displayName: string; email: string }
  ): Registration[] {
    const ctx = userContext ? { displayName: userContext.displayName, approvedBy: userContext.approvedBy } : undefined
    return registrationsData.map((o, index) => new Registration(o, registrationsData.length - index, service, ctx))
  }

  /**
   * selects/ unselects a registration
   * @param value boolean
   */
  public toggleSelect(value: boolean) {
    this.selected = value
    this.changed$.next(null)
  }
  /**
   * Gets the current flag color from third_parts.flags or falls back to flagged property
   */
  public getFlagColor(): 'red' | 'orange' | 'green' | null {
    // Only show flag color if flagged is true (active flag)
    // This allows history to be preserved even when flag is removed
    if (this.flagged && this._source.third_parts?.flags && Array.isArray(this._source.third_parts.flags) && this._source.third_parts.flags.length > 0) {
      return this._source.third_parts.flags[0].flag as 'red' | 'orange' | 'green'
    }
    return this.flagged ? 'red' : null
  }

  /**
   * Gets all flags/comments from third_parts
   */
  public getFlags(): Array<{ flag: string; time: string; by: string; comment?: string }> {
    return this._source.third_parts?.flags || []
  }

  /**
   * Checks if there are any comments
   */
  public hasComments(): boolean {
    const flags = this.getFlags()
    return flags.some((f) => f.comment && f.comment.trim().length > 0)
  }

  /**
   * Gets the count of comments on flags
   */
  public getCommentCount(): number {
    const flags = this.getFlags()
    return flags.filter((f) => f.comment && f.comment.trim().length > 0).length
  }

  /**
   * Flags/ unflags a registration - async
   */
  public flag() {
    this.processing = true
    this.flagged = !this.flagged
    this.changed$.next(null)
    this.service.toggleFlag(this._source.event, this._source.form, this._source.id, this.flagged).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.flagged = !this.flagged
        this.processing = false
        this.changed$.next(null)
      }
    )
  }

  /**
   * Sets a colored flag with optional comment
   */
  public setColoredFlag(color: 'red' | 'orange' | 'green', comment?: string) {
    this.processing = true
    this.changed$.next(null)

    const userName = this._userContext?.displayName || 'Unknown'

    const flags = this._source.third_parts?.flags || []
    const newFlag = {
      flag: color,
      time: new Date().toISOString(),
      by: userName,
      ...(comment ? { comment } : {})
    }

    // Add to beginning of array
    const updatedFlags = [newFlag, ...flags]

    // Ensure third_parts exists and preserve existing data
    if (!this._source.third_parts) {
      this._source.third_parts = {
        max_registration: this.maximum_registrations || 0
      }
    }
    // Update flags in third_parts
    this._source.third_parts.flags = updatedFlags

    // Update flagged property for backward compatibility
    this.flagged = true

    // Send only the flags array - backend will merge it with existing third_parts
    this.service.updateFlags(this._source.event, this._source.form, this._source.id, updatedFlags).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        // Revert on error
        if (flags.length === 0) {
          delete this._source.third_parts.flags
        } else {
          this._source.third_parts.flags = flags
        }
        this.processing = false
        this.changed$.next(null)
      }
    )
  }

  /**
   * Removes flag - clears current flag status but keeps history/comments
   */
  public removeFlag() {
    this.processing = true
    this.changed$.next(null)

    const flags = this._source.third_parts?.flags || []

    // Keep flags array (preserves history/comments) but clear flagged status
    // The flags array stays intact so history is preserved
    this.flagged = false

    // Send the existing flags array (preserves history) with unflag=true
    // Backend will set tags.flagged = false while preserving the flags array
    this.service.updateFlags(this._source.event, this._source.form, this._source.id, flags, true).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        // Revert on error
        this.flagged = flags.length > 0
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * Changes the status of a registration
   * @param status - The approval status to change to
   * When status is APPROVED, optimistically updates the approvedBy field with current user's name from localStorage
   */
  public changeStatusTo(status: ApprovalStatus) {
    const current = this.entry_status
    const previousApprovedBy = this.approvedBy
    const previousDeclinedBy = this.declinedBy
    this.processing = true
    this.entry_status = status

    if (status === ApprovalStatus.APPROVED && this._userContext?.approvedBy) {
      this.approvedBy = this._userContext.approvedBy
    }
    if (status === ApprovalStatus.DECLINED && this._userContext?.approvedBy) {
      this.declinedBy = this._userContext.approvedBy
    }

    this.changed$.next(null)
    this.service.approveRegistration(this._source.event, this._source.form, this._source.id, this.entry_status !== undefined ? this.entry_status : this.approved).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.entry_status = current
        this.approvedBy = previousApprovedBy
        this.declinedBy = previousDeclinedBy
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * disapproves a registration
   */
  public disapprove() {
    const current = this.approved
    this.processing = true
    this.approved = ApprovalStatus.DECLINED
    this.changed$.next(null)
    this.service.approveRegistration(this._source.event, this._source.form, this._source.id, this.approved).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.approved = current
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * Marks a registration as attended - global zone
   */
  public attend(spotSlug = 'global') {
    const current = this.attended
    this.processing = true
    this.attended = true
    this.changed$.next(null)
    this.service.attendRegistration(this._source.event, spotSlug, this._source.id, this.attended).subscribe(
      () => {
        this.attendanceDays.push(new Date().toISOString().slice(0, 10))
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.attended = current
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * unattends a registrations - global zone
   */
  public unattend() {
    const current = this.attended
    this.processing = true
    this.attended = false
    this.changed$.next(null)
    this.service.attendRegistration(this._source.event, 'global', this._source.id, this.attended).subscribe(
      () => {
        const today = new Date().toISOString().slice(0, 10)
        const index = this.attendanceDays.indexOf(today)
        if (index !== -1) this.attendanceDays.splice(index, 1)
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.attended = current
        this.processing = false
        this.changed$.next(null)
      }
    )
  }

  /**
   * refund a registration
   */
  public refund(amount: number, decline: boolean) {
    this.processing = true
    this.changed$.next(null)
    this.service.refund(this._source.event, this._source.form, this._source.id, amount, decline).subscribe(
      () => {
        this.processing = false
        if (decline) this.entry_status = ApprovalStatus.DECLINED
        this.refunded = true
        this.payments.forEach((payment) => {
          payment.refund_amount = payment.amount
        })
        this.service.toastrService.success('Success', 'Refund successful')
        this.changed$.next(null)
      },
      (error) => {
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * Resends confirmation via Email or SMS (async). Prefer the database resend flow UI for channel selection.
   */
  resendConfirmation(channel: ResendConfirmationSingleChannel = NotificationChannel.EMAIL) {
    this.processing = true
    this.changed$.next(null)
    this.service.resendConfirmation(this._source.event, this.id, channel, channel === NotificationChannel.WHATSAPP ? { formSlug: this._source.form } : undefined).subscribe({
      next: () => {
        this.processing = false
        if (channel === NotificationChannel.EMAIL && this._source.mail_status) {
          this._source.mail_status.pending_mail_count++
          this.calculateMailStatus(true)
        }
        this.service.toastrService.success('Success', resendConfirmationSuccessToastBody(channel))
        setTimeout(() => {
          this.updateSelf()
        }, 10000)
        this.changed$.next(null)
      },
      error: () => {
        this.processing = false
        this.changed$.next(null)
      }
    })
  }

  /**
   * After editing a registration it's crucial to retrieve it's new data and update the current class
   * update self does just that, it's also fired when an email is sent - will keep pooling each 10s
   */
  public updateSelf() {
    this.processing = true
    this.changed$.next(null)
    this.service.getRegistration(this._source.event, this._source.form, this._source.id).subscribe(
      (_updatedSource) => {
        this._source.fields = _updatedSource.fields
        if (this._source.mail_status) {
          this._source.mail_status = _updatedSource.mail_status
          this.calculateMailStatus(true)
          if (this.mailStatus === 'Pending') {
            setTimeout(() => {
              this.updateSelf()
            }, 10000)
          }
        }
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /** Deletes a registration
   * @deprecated
   */
  public deleteSelf() {
    confirm('Would you like to delete the registrant ' + this.fields.fullname)
  }

  /**
   * Sets the selected seats property
   */
  private _setSelectedSeats() {
    let seats = []
    if (this._source.products && this._source.products.length) {
      for (const product of this._source.products) {
        if (product.selected_seats) {
          seats = [...seats, ...product.selected_seats]
        }
      }
    }
    this.selectedSeats = seats.join(', ')
  }

  /**
   * Gets the child persona for the registration
   * @returns Persona
   */
  private _getChildPersona(): Persona {
    const parentPersona = this._form
    if (parentPersona.type !== PersonaTypes.GROUP) return null
    return this.service.forms.value.find((form) => form.type === PersonaTypes.MEMBER && form.parent === parentPersona.id)
  }

  /**
   * Total number of badge prints for this registration.
   */
  public get printCount(): number {
    return this.prints.length
  }

  /**
   * Whether the registration has any print history.
   */
  public hasPrintHistory(): boolean {
    return this.printCount > 0
  }

  /**
   * Latest print entry sorted by known timestamp keys.
   */
  public get latestPrint(): RegistrationPrintEntry | null {
    if (!this.prints.length) return null
    return this.prints[this.printCount - 1] || null
  }

  /**
   * Human-readable person for latest print.
   */
  public get latestPrintPerson(): string {
    const latest = this.latestPrint
    if (!latest) return 'Not printed'
    const person = this._extractPrintPersonName(latest?.printed_by)
    return person || 'Unknown'
  }

  /**
   * Latest print timestamp (raw), useful for formatting in templates.
   */
  public get latestPrintAt(): string | null {
    const latest = this.latestPrint
    if (!latest) return null
    return latest.printed_at || null
  }

  /**
   * Whether the latest print was done via facial recognition.
   */
  public get latestPrintViaFacialRecognition(): boolean {
    return !!this.latestPrint?.facialRecognition
  }

  /**
   * Name for the Approved / Declined By column.
   * Declined → `declined_by.name`. Approved → `approved_by.name` or "Auto approved".
   */
  public get approvedOrDeclinedByDisplay(): string {
    if (this.entry_status === ApprovalStatus.DECLINED) {
      return this.declinedBy?.name || ''
    }
    if (this.entry_status === ApprovalStatus.APPROVED) {
      return this.approvedBy?.name || 'Auto approved'
    }
    return ''
  }

  private _extractPrintPersonName(value: RegistrationPrintEntry['printed_by']): string {
    if (!value) return ''
    return this._normalizePrintPerson(value.name || '')
  }

  private _normalizePrintPerson(person: string): string {
    if (!person) return ''
    const normalized = person.toLowerCase().trim()
    if (normalized === 'self_print' || normalized === 'self_register') return 'Self Printed'
    return person.trim()
  }
}
